import React, { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { boldText, linkText, mailToLink } from '@utils/intlFormatterUtils';

interface ITermsAndConditions {
	header: string;
	text: string[] | ReactNode[];
	multiLine: IMultiLevelConditions;
}

interface IMultiLevelConditions {
	isMultiLevel: boolean;
	list?: string[];
	endText?: string[];
}

const ConditionsTextBlock = (props: any) => {
	return (
		<>
			{props.text.map((element: string, index: number) => {
				return (
					<p key={index} className='mt-3 w-full text-justify font-normal'>
						{element}
					</p>
				);
			})}
		</>
	);
};

const TermsAndConditionsBlock = ({ header, text, multiLine }: ITermsAndConditions) => {
	return (
		<div className='pt-4'>
			<h2 className='text-xl mt-1 mb-2' style={{ fontFamily: "'Montserrat', normal" }}>
				{header}
			</h2>
			{text ? <ConditionsTextBlock text={text} /> : null}
			<ul className='ml-10 text-justify'>
				{multiLine.list?.map((element, index) => {
					return (
						<li className='list-disc' key={index}>
							{element}
						</li>
					);
				})}
			</ul>
			{multiLine?.endText ? <ConditionsTextBlock text={multiLine.endText} /> : null}
		</div>
	);
};

const TermsAndConditions = () => {
	const { formatMessage } = useIntl();

	return (
		<div className='container mb-5 mx-auto mt-22 flex justify-center'>
			<div className='lg:w-[70%] w-[95%]'>
				<div className='text-2xl 2xl:text-4.5xl' style={{ fontFamily: "'Montserrat', normal" }}>
					<h1 className='text-2xl mt-2 mb-4'>
						{formatMessage({ id: 'bcs-Terms-Conditions-Title' }, { b: boldText }) as string}
					</h1>
				</div>
				<TermsAndConditionsBlock
					header={formatMessage({ id: 'bcs-I-Terms-Title' }, { b: boldText }) as string}
					text={[]}
					multiLine={{
						isMultiLevel: true,
						list: [
							formatMessage({ id: 'bcs-I-Text-I-multilevel-I' }),
							formatMessage({ id: 'bcs-I-Text-I-multilevel-II' }),
							formatMessage({ id: 'bcs-I-Text-I-multilevel-III' }),
							formatMessage({ id: 'bcs-I-Text-I-multilevel-IV' }),
							formatMessage({ id: 'bcs-I-Text-I-multilevel-V' }),
							formatMessage({ id: 'bcs-I-Text-I-multilevel-VI' }),
						],
					}}
				/>
				<TermsAndConditionsBlock
					header={formatMessage({ id: 'bcs-I-Terms-Conditions-Title' }, { b: boldText }) as string}
					text={[formatMessage({ id: 'bcs-I-Text' })]}
					multiLine={{
						isMultiLevel: false,
					}}
				/>
				<TermsAndConditionsBlock
					header={''}
					text={[
						formatMessage({ id: 'bcs-Owner-Platform-Title' }),
						formatMessage({ id: 'bcs-Dot' }),
						formatMessage({ id: 'bcs-Owner-Information-I' }),
						formatMessage({ id: 'bcs-Owner-Information-II' }),
						formatMessage({ id: 'bcs-Owner-Information-III' }, { mailToLink: mailToLink }),
					]}
					multiLine={{
						isMultiLevel: false,
					}}
				/>
				<TermsAndConditionsBlock
					header={formatMessage({ id: 'bcs-II-Terms-Conditions-Title' }, { b: boldText }) as string}
					text={[
						formatMessage({ id: 'bcs-II-Text-I' }),
						formatMessage({ id: 'bcs-II-Text-II' }),
						formatMessage({ id: 'bcs-II-Text-III' }),
						formatMessage({ id: 'bcs-II-Text-IV' }),
					]}
					multiLine={{
						isMultiLevel: true,
						list: [
							formatMessage({ id: 'bcs-II-Text-IV-multilevel-I' }),
							formatMessage({ id: 'bcs-II-Text-IV-multilevel-II' }),
							formatMessage({ id: 'bcs-II-Text-IV-multilevel-III' }),
							formatMessage({ id: 'bcs-II-Text-IV-multilevel-IV' }),
							formatMessage({ id: 'bcs-II-Text-IV-multilevel-V' }),
							formatMessage({ id: 'bcs-II-Text-IV-multilevel-VI' }),
							formatMessage({ id: 'bcs-II-Text-IV-multilevel-VII' }),
						],
						endText: [formatMessage({ id: 'bcs-II-Text-VI' }), formatMessage({ id: 'bcs-II-Text-VII' })],
					}}
				/>
				<TermsAndConditionsBlock
					header={formatMessage({ id: 'bcs-III-Terms-Conditions-Title' }, { b: boldText }) as string}
					text={[
						formatMessage({ id: 'bcs-III-Text-I' }),
						formatMessage({ id: 'bcs-III-Text-II' }),
						formatMessage({ id: 'bcs-III-Text-III' }),
						formatMessage({ id: 'bcs-III-Text-IV' }),
						formatMessage(
							{ id: 'bcs-III-Text-V' },
							{
								a: (text: any) => linkText(text, '/privacy-policy'),
								f: (text: any) => linkText(text, 'https://developers.facebook.com/terms/'),
							}
						) as string,
						formatMessage({ id: 'bcs-III-Text-VI' }),
						formatMessage({ id: 'bcs-III-Text-VII' }),
						formatMessage({ id: 'bcs-III-Text-VIII' }),
					]}
					multiLine={{
						isMultiLevel: false,
					}}
				/>
				<TermsAndConditionsBlock
					header={formatMessage({ id: 'bcs-IV-Terms-Conditions-Title' }, { b: boldText }) as string}
					text={[
						formatMessage({ id: 'bcs-IV-Text-I' }),
						formatMessage({ id: 'bcs-IV-Text-II' }, { br: <br /> }),
						formatMessage({ id: 'bcs-IV-Text-III' }),
						formatMessage({ id: 'bcs-IV-Text-IV' }),
						formatMessage({ id: 'bcs-IV-Text-V' }, { br: <br /> }),
						formatMessage({ id: 'bcs-IV-Text-VI' }),
						formatMessage({ id: 'bcs-IV-Text-VII' }),
						formatMessage({ id: 'bcs-IV-Text-VIII' }),
						formatMessage({ id: 'bcs-IV-Text-IX' }),
						formatMessage({ id: 'bcs-IV-Text-X' }),
					]}
					multiLine={{
						isMultiLevel: false,
					}}
				/>
				<TermsAndConditionsBlock
					header={formatMessage({ id: 'bcs-V-Terms-Conditions-Title' }, { b: boldText }) as string}
					text={[
						formatMessage({ id: 'bcs-V-Text-I' }),
						formatMessage({ id: 'bcs-V-Text-II' }),
						formatMessage({ id: 'bcs-V-Text-III' }),
					]}
					multiLine={{
						isMultiLevel: false,
					}}
				/>
				<TermsAndConditionsBlock
					header={formatMessage({ id: 'bcs-VI-Terms-Conditions-Title' }, { b: boldText }) as string}
					text={[
						formatMessage({ id: 'bcs-VI-Text-I' }),
						formatMessage({ id: 'bcs-VI-Text-II' }, { mailToLink: mailToLink }),
						formatMessage({ id: 'bcs-VI-Text-III' }),
						formatMessage({ id: 'bcs-VI-Text-IV' }),
						formatMessage({ id: 'bcs-VI-Text-V' }),
						formatMessage({ id: 'bcs-VI-Text-VI' }),
						formatMessage(
							{ id: 'bcs-VI-Text-VII' },
							{ link: (text: any) => linkText(text, 'https://www.kzp.bg') }
						) as string,
						formatMessage({ id: 'bcs-VI-Text-VIII' }),
						formatMessage({ id: 'bcs-VI-Text-IX' }),
						formatMessage({ id: 'bcs-VI-Text-X' }),
					]}
					multiLine={{
						isMultiLevel: false,
					}}
				/>
			</div>
		</div>
	);
};

export default TermsAndConditions;
